import React, { useEffect, useState, useRef } from 'react';
import { patientApi } from '../services/api';
import useLhcFormsScripts from '../hooks/useLhcFormsScripts';
import useCheckIn from './redux/hooks/useCheckIn';

/**
 * LhcFormRenderer component for rendering a single LHC-Forms form instance.
 * This component handles fetching the form definition, loading saved data,
 * and interacting with the <wc-lhc-form> web component.
 * Enhanced with timing fixes for reliable form loading.
 *
 * @param {Object} props - Component props
 * @param {Object} props.formDetail - Details about the form, including form_id, version, and optionally schema and submissionId.
 * @param {Object} props.tokenData - Patient token data, including patient_id (sub).
 * @param {Function} props.onSuccess - Callback function to call on successful final submission.
 * @returns {JSX.Element} The LHC-Forms renderer component.
 */
const LhcFormRenderer = ({ formDetail, tokenData, onSuccess }) => {
  console.log('LhcFormRenderer: Received props:', { formDetail, tokenData });
  console.log('LhcFormRenderer: Form type:', formDetail?.type?.name);
  console.log('LhcFormRenderer: Form data:', formDetail?.formData);
  console.log('LhcFormRenderer: Schema available:', !!formDetail?.schema);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submissionId, setSubmissionId] = useState(formDetail?.submissionId || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSchema, setFormSchema] = useState(formDetail?.schema || null);
  const [isInitialized, setIsInitialized] = useState(false); // State to track if form is initialized
  const [questionnaireResponseState, setQuestionnaireResponseState] = useState(null); // State to store QuestionnaireResponse

  // Reference to the web component instance
  const lhcFormRef = useRef(null);
  const isInitializedRef = useRef(false); // Ref to track if the form has been initialized

  // Get check-in state and actions from Redux
  const { updateCurrentFormId, updateCurrentFormVersion } = useCheckIn();

  // Load LHC Forms scripts dynamically
  const { isLoaded: scriptsLoaded, error: scriptsError } = useLhcFormsScripts();

  // Store form ID and version in Redux when component mounts
  useEffect(() => {
    if (formDetail?.form_id) {
      updateCurrentFormId(formDetail.form_id);
      updateCurrentFormVersion(formDetail.version || formDetail.original_version);
    }
  }, [formDetail?.form_id, formDetail?.version, formDetail?.original_version, updateCurrentFormId, updateCurrentFormVersion]);

  // Effect to fetch form schema if not provided in formDetail
  useEffect(() => {

    // If schema is already provided, use it and stop loading
    if (formDetail?.schema && Object.keys(formDetail.schema).length > 0) {
      console.log('LhcFormRenderer: Using schema from formDetail');
      setFormSchema(formDetail.schema);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch the schema from the backend
    const fetchFormSchema = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log('LhcFormRenderer: Fetching form schema from backend for form ID', formDetail?.form_id, 'and version', formDetail?.version);

        const patientId = tokenData?.sub || tokenData?.patient_id;
        if (!patientId) {
          console.error('LhcFormRenderer: Missing patient ID in token data');
          setError('Error PIN-25525: Patient ID is missing. Please clear your browser history (or use a new browser window), and try again. Click Support Desk from Menu for details.');
          setIsLoading(false);
          return;
        }

        // Fetch the form definition directly
        const formDataResponse = await patientApi.getFormData(
          patientId,
          formDetail?.form_id,
          null, // submissionId is not needed for fetching the latest schema
          formDetail?.version || null,
          tokenData
        );

        if (formDataResponse?.data?.success && formDataResponse.data.schema) {
          console.log('LhcFormRenderer: Successfully fetched schema directly');
          const fetchedSchema = formDataResponse.data.schema;

          // Deep copy the schema to avoid mutation issues
          const modifiedSchema = JSON.parse(JSON.stringify(fetchedSchema));

          // Find the medications section and the med_strength item
          const medicationsSection = modifiedSchema.items.find(item => item.linkId === '/medications');
          if (medicationsSection && medicationsSection.items) {
            const medStrengthItem = medicationsSection.items.find(item => item.linkId === '/medications/med_strength');
            if (medStrengthItem) {
              // Ensure dataControl exists and is an array
              if (!medStrengthItem.dataControl || !Array.isArray(medStrengthItem.dataControl)) {
                medStrengthItem.dataControl = [];
              }

              // Check if dataControl already has the correct sourceLinkId
              const existingDataControl = medStrengthItem.dataControl.find(dc =>
                dc.source?.sourceType === 'INTERNAL' && dc.source?.sourceLinkId === '/medications/med_name'
              );

              if (!existingDataControl) {
                 // Add the dataControl with dataFormat if it doesn't exist
                medStrengthItem.dataControl.push({
                  "source": {
                    "sourceType": "INTERNAL",
                    "sourceLinkId": "/medications/med_name"
                  },
                  "construction": "ARRAY",
                  "dataFormat": {
                    "code": "value.data.RXCUIS",
                    "text": "value.data.STRENGTHS_AND_FORMS"
                  },
                  "onAttribute": "answers"
                });
                 console.log('Added dataFormat to /medications/med_strength dataControl');
              } else {
                 // If dataControl exists, ensure dataFormat is present
                 if (!existingDataControl.dataFormat) {
                    existingDataControl.dataFormat = {
                       "code": "value.data.RXCUIS",
                       "text": "value.data.STRENGTHS_AND_FORMS"
                    };
                     console.log('Added dataFormat to existing /medications/med_strength dataControl');
                 } else {
                     console.log('dataFormat already exists in /medications/med_strength dataControl');
                 }
              }


              // Update displayControl to drop-down
              if (!medStrengthItem.displayControl) {
                  medStrengthItem.displayControl = {};
              }
              if (!medStrengthItem.displayControl.answerLayout) {
                  medStrengthItem.displayControl.answerLayout = {};
              }
              medStrengthItem.displayControl.answerLayout.type = "drop-down";
              console.log('Updated /medications/med_strength displayControl to drop-down');

            } else {
              console.warn('LhcFormRenderer: /medications/med_strength item not found in schema');
            }
          } else {
            console.warn('LhcFormRenderer: /medications section not found in schema');
          }

          setFormSchema(modifiedSchema);
        } else {
          console.error('LhcFormRenderer: Failed to fetch schema directly:', formDataResponse?.data?.message || 'Unknown error');
          setError(`Could not load form definition for form ID ${formDetail?.form_id}.`);
        }
      } catch (error) {
        console.error('LhcFormRenderer: Error fetching form schema:', error);
        setError('An unexpected error occurred while loading the form definition.');
      } finally {
        setIsLoading(false);
      }
    };

    if (formDetail?.form_id) {
      fetchFormSchema();
    } else {
      setError('Form ID is missing.');
      setIsLoading(false);
    }

  }, [formDetail, tokenData]); // Depend on formDetail and tokenData

  // Effect to initialize the form after the component mounts and schema is available
  // Enhanced with timing fixes to handle ref availability
  useEffect(() => {
    console.log('LhcFormRenderer: useEffect triggered with:', {
      formSchema: !!formSchema,
      lhcFormRef: !!lhcFormRef.current,
      windowLForms: !!window.LForms,
      formDetail,
      formType: formDetail?.type?.name
    });

    // If ref is not ready yet, wait for it
    if (!lhcFormRef.current) {
      console.log('LhcFormRenderer: Web component ref not ready, will retry...');
      const checkRef = () => {
        if (lhcFormRef.current && formSchema && window.LForms) {
          console.log('LhcFormRenderer: Ref is now ready, initializing form');
          initializeFormWithRef();
        }
      };
      
      // Check periodically for ref to become available
      const refCheckInterval = setInterval(() => {
        if (lhcFormRef.current) {
          clearInterval(refCheckInterval);
          checkRef();
        }
      }, 50); // Check every 50ms
      
      // Clear interval after 5 seconds to prevent infinite checking
      setTimeout(() => clearInterval(refCheckInterval), 5000);
      return;
    }

    if (lhcFormRef.current && formSchema && scriptsLoaded) {
      initializeFormWithRef();
    }

    function initializeFormWithRef() {
      // Prevent re-initialization if already initialized
      if (isInitializedRef.current) {
        console.log('LhcFormRenderer: Form already initialized, skipping re-initialization.');
        return;
      }
      console.log('Initializing LHC form with schema:', formSchema);

      // Make sure LForms library is loaded
      if (window.LForms) {
        try {
          // Clear previous form content if any
          if (lhcFormRef.current) {
            lhcFormRef.current.innerHTML = '';
          }
          
          // Set FHIR context before using any FHIR-related functions
          if (window.LForms.Util.setFHIRContext) {
            const fhirVersion = formSchema?.fhirVersion || 'R4';
            console.log(`Setting FHIR context to version: ${fhirVersion}`);
            window.LForms.Util.setFHIRContext(fhirVersion);
          }
          
          console.log('Initializing form with schema and options');

          // If we have form data, create a FHIR QuestionnaireResponse
          const questionnaireResponse = formDetail.formData;

          window.LForms.Util.addFormToPage(formSchema, lhcFormRef.current, {questionnaireResponse: questionnaireResponse});
          console.log('Form initialized successfully with schema and QuestionnaireResponse '+ questionnaireResponse);
          isInitializedRef.current = true; // Mark as initialized
          setIsInitialized(true); // Update state to trigger the data loading effect;

          // Add event listener for changes to log data
          lhcFormRef.current.addEventListener('change', (event) => {
            console.log('LHC Form Change Event:', event);
            if (window.LForms && window.LForms.Util) {
              // Attempt to get item data from the event target
              const itemData = window.LForms.Util.getItemDataFromElement ? window.LForms.Util.getItemDataFromElement(event.target) : 'getItemDataFromElement not available';
              console.log('LHC Form Item Data from Element:', itemData);

              // Also log the full form data
              if (window.LForms.Util.getFormData) {
                const currentFormData = window.LForms.Util.getFormData(lhcFormRef.current, false, false);
                console.log('Current Form Data:', currentFormData);
              }
            }
          });
          
          // After form is initialized, try to load form data if available
          if (formDetail?.formData && Object.keys(formDetail.formData).length > 0) {
            console.log('Will attempt to load form data after initialization:', formDetail.formData);
            // We'll handle this in a separate useEffect
          }
          
        } catch (error) {
          console.error('Error initializing form:', error);
          setError('Error initializing form: ' + error.message);
        }
      } else {
        console.error('LForms library not loaded');
        setError('LForms library not loaded. Please check if the library is included in the page.');
      }
    }
  }, [formSchema, formDetail?.form_id, formDetail?.formData, tokenData?.patient_id, scriptsLoaded]); // Re-run when formSchema, formId, formData, patientId, or scriptsLoaded changes

  // Effect to load form data after the form has been initialized
  useEffect(() => {
    // Only proceed if the form is initialized and we have form data
    if (isInitialized && lhcFormRef.current && formDetail?.formData && Object.keys(formDetail.formData).length > 0) {
      console.log('Attempting to load form data after initialization:', formDetail.formData);
      // If we have form data, create a FHIR QuestionnaireResponse
      let questionnaireResponse;
      if (formDetail?.formData && Object.keys(formDetail.formData).length > 0) {
        console.log('Adding form data to options:', formDetail.formData);
        
        // Check if formData is already a properly formatted QuestionnaireResponse
        if (formDetail.formData.resourceType === "QuestionnaireResponse" &&
            formDetail.formData.item &&
            formDetail.formData.status) {
          console.log('Using existing QuestionnaireResponse from formData');
          questionnaireResponse = formDetail.formData;
          window.LForms.Util.addFormToPage(formSchema, lhcFormRef.current, {questionnaireResponse: questionnaireResponse});
          console.log('Form initialized successfully with schema and QuestionnaireResponse '+ questionnaireResponse);
            }
        else {
          window.LForms.Util.addFormToPage(formSchema, lhcFormRef.current,'');
          console.log('Form initialized successfully with schema without QuestionnaireResponse');
        }
      } else {
        console.log('Critical function error. Aborting.');
      }
      // Wait a bit to ensure the form is fully rendered
      setTimeout(() => {
        // TEMPORARILY DISABLED: The mergeFHIRDataIntoLForms function call was causing errors due to incorrect parameter order
        // The expected parameter order should be: (resourceType, fhirData, formData, fhirVersion)
        // This functionality is disabled until the correct implementation can be determined
        
        // Original code commented out:
        /*
        try {
          if (window.LForms && window.LForms.Util && window.LForms.Util.mergeFHIRDataIntoLForms) {
            console.log('Merging FHIR data into form using mergeFHIRDataIntoLForms');
            const fhirVersion = formSchema?.fhirVersion || 'R4';
            // Create a FHIR QuestionnaireResponse from the form data
            const questionnaireResponse = {
              resourceType: "QuestionnaireResponse",
              status: "in-progress",
              item: Object.entries(formDetail.formData).map(([key, value]) => ({
                linkId: key,
                answer: [{ valueString: value ? value.toString() : "" }]
              }))
            };
            window.LForms.Util.mergeFHIRDataIntoLForms(
              questionnaireResponse,
              fhirVersion,
              lhcFormRef.current
            );
            console.log('FHIR form data merged successfully.');
          } else {
            console.warn('LForms library or mergeFHIRDataIntoLForms method not available for loading data after initialization.');
          }
        } catch (error) {
          console.error('Error loading form data after initialization:', error);
        }
        */
      }, 1000); // Give the form time to fully initialize
    }
  }, [formDetail?.formData, isInitialized, formSchema]); // Depend on formData, isInitialized, and formSchema

  // Effect to implement Autosave to Local Storage (using a debounce)
  useEffect(() => {
    let debounceTimeout = null;

    const handleFormChange = () => {
      if (lhcFormRef.current && window.LForms && window.LForms.Util && window.LForms.Util.getFormFHIRData) {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          try {
            const fhirVersion = formSchema?.fhirVersion || 'R4';
            const currentFormData = window.LForms.Util.getFormData(lhcFormRef.current, false, false); // https://lhncbc.github.io/lforms/index.html#validation
            const patientId = tokenData?.sub || tokenData?.patient_id;
            const localStorageKey = `lforms_data_${patientId}_${formDetail?.form_id}`;
            localStorage.setItem(localStorageKey, JSON.stringify(currentFormData));
            console.log(`Autosaved form data for ${formDetail?.form_id} to localStorage.`);
          } catch (saveError) {
            console.error('Error during autosave:', saveError);
          }
        }, 1000); // Debounce for 1 second
      }
    };

    // Attach event listener to the web component
    const lhcFormElement = lhcFormRef.current;
    if (lhcFormElement) {
      // Assuming the web component emits a 'change' or similar event
      // You might need to check the actual event name provided by the LForms web component
      lhcFormElement.addEventListener('change', handleFormChange);
    }

    return () => {
      // Cleanup: remove event listener and clear timeout
      if (lhcFormElement) {
        lhcFormElement.removeEventListener('change', handleFormChange);
      }
      clearTimeout(debounceTimeout);
    };
  }, [formSchema, formDetail?.form_id, tokenData?.patient_id]); // Depend on formSchema, formId, and patientId

  // Implement Save & Resume (API Submission)
  const handleSaveAndResume = async () => {
    if (lhcFormRef.current && window.LForms && window.LForms.Util && window.LForms.Util.getFormFHIRData) {
      setIsSubmitting(true);
      setError(null);
      try {
        const currentFormData = window.LForms.Util.getFormFHIRData('QuestionnaireResponse', 'R4', lhcFormRef.current, '')
        // OR if that doesn't work, use the following:
        // const fhirVersion = formSchema?.fhirVersion || 'R4';
        // const resourceType = formSchema?.resourceType || 'QuestionnaireResponse';
        // const finalFormData = window.LForms.Util.getFormFHIRData(resourceType, fhirVersion, lhcFormRef.current, options)
        // https://lhncbc.github.io/lforms/#userData
        const patientId = tokenData?.sub || tokenData?.patient_id;
        const formId = formDetail?.form_id;
        const formVersion = formDetail?.version || null;

        if (!patientId || !formId) {
           console.error('Missing data for saving:', { patientId, formId, tokenData });
           setError("Missing patient ID or form ID for saving.");
           setIsSubmitting(false);
           return;
        }

        const saveResponse = await patientApi.submitFormData(
          patientId,
          formId,
          currentFormData,
          submissionId, // Use current submissionId state
          false, // updateData=false for "Save & Resume" (creates a new version or updates existing if submissionId is present)
          formVersion,
          tokenData
        );

        if (saveResponse.data && saveResponse.data.success) {
          console.log('Form data saved successfully via API:', saveResponse.data);
          // Update submissionId state if a new one was created by the backend
          if (saveResponse.data.submission_id) {
             setSubmissionId(saveResponse.data.submission_id);
          }
          // Optionally show a success message to the user
        } else {
          setError(saveResponse.data.message || 'Failed to save form data.');
        }
      } catch (saveError) {
        console.error('Error saving form data:', saveError);
        setError('An error occurred while saving your form.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setError("LForms library not loaded or form not ready.");
    }
  };

  // Implement Final Submission
  const handleSubmit = async () => {
    if (lhcFormRef.current && window.LForms && window.LForms.Util && window.LForms.Util.getFormFHIRData) {
      setIsSubmitting(true);
      setError(null);
      try {
        // Optional: Perform form validation before submission
        // if (window.LForms.Util.checkValidity && !window.LForms.Util.checkValidity(lhcFormRef.current)) {
        //      setError("Please fill out all required fields.");
        //      setIsSubmitting(false);
        //      return;
        // }

        const finalFormData = window.LForms.Util.getFormFHIRData('QuestionnaireResponse', 'R4', lhcFormRef.current, '')
        const patientId = tokenData?.sub || tokenData?.patient_id;
        const formId = formDetail?.form_id;
        const formVersion = formDetail?.version || null;

         if (!patientId || !formId) {
           console.error('Missing data for submission:', { patientId, formId, tokenData });
           setError("ERROR PIN-42122: Missing patient ID or form ID for submission. Contact Support Desk from Menu for assistance");
           setIsSubmitting(false);
           return;
        }

        const submitResponse = await patientApi.submitFormData(
          patientId,
          formId,
          finalFormData,
          submissionId, // Use current submissionId state
          true, // updateData=true for final submission (overwrite existing submission)
          formVersion,
          tokenData
        );

        if (submitResponse.data && submitResponse.data.success) {
          console.log('Form data submitted successfully:', submitResponse.data);
          // Clear local storage data for this form after successful submission
          const patientId = tokenData?.sub || tokenData?.patient_id;
          const localStorageKey = `lforms_data_${patientId}_${formDetail?.form_id}`;
          localStorage.removeItem(localStorageKey);
          // Call the onSuccess callback provided by the parent component
          onSuccess({
             formId: formDetail?.form_id,
             submissionId: submitResponse.data.submission_id || submissionId,
             formData: finalFormData // Optionally pass submitted data back
          });
        } else {
          setError(submitResponse.data.message || 'Failed to submit form data.');
        }
      } catch (submitError) {
        console.error('Error submitting form data:', submitError);
        setError('An error occurred while submitting your form.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setError("LForms library not loaded or form not ready.");
    }
  };


  if (isLoading || !scriptsLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading form...</span>
        </div>
      </div>
    );
  }

  if (error || scriptsError) {
    return <div className="alert alert-danger">{error || scriptsError}</div>;
  }

  // Render the form only when schema is available and not loading/errored
  if (!formSchema) {
      return <div className="alert alert-warning">Form definition is missing after loading attempt.</div>;
  }


  return (
    <div>
      {/* The LHC form will be rendered into this div */}
      <div id="lhc-form-container">
        {scriptsLoaded ? (
          <div ref={lhcFormRef}></div>
        ) : (
          <div className="text-center p-3">
            <div className="spinner-border spinner-border-sm text-secondary" role="status">
              <span className="visually-hidden">Loading form component...</span>
            </div>
            <p className="mt-2 text-muted">Initializing form...</p>
          </div>
        )}
      </div>

      {/* Add Save & Resume and Submit buttons */}
      <div className="form-actions mt-3">
        <button className="btn btn-secondary me-2" onClick={handleSaveAndResume} disabled={isSubmitting || !scriptsLoaded}>
          {isSubmitting ? 'Saving...' : 'Save & Resume'}
        </button>
        <button className="btn btn-primary" onClick={handleSubmit} disabled={isSubmitting || !scriptsLoaded}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
};

export default LhcFormRenderer;